import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { object, string, ref } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { changeUser, fetchUserInfo, resetStatus } from "../redux/usersSagas";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import { ErrorList } from "../../../../components/ErrorList";
import { DataPermissions } from "./DataPermissions";
import { AccessPermissions } from "./AccessPermissions";
import { UserInfo } from "./UserInfo";
import { useError } from "../hooks/useError";
import { showToast } from "../../../../utils/showToast";
import { passwordSchemaTest } from "../../../../utils/password-utils";
import {
  extendPermissionByCustom,
  filterHiddenPermissionGroups,
  rearrangeDailyEmailsPermissions
} from "../../../../utils/permissionsUtils";

const schema = object({
  first_name: string().label("First Name").trim().min(2).required(),
  last_name: string().label("Last Name").trim().min(2).required(),
  email: string()
    .label("Email")
    .trim()
    .when({
      is: (value) => !!value,
      then: (schema) => schema.email()
    }),
  password: string()
    .label("Password")
    .when({ is: (value) => !!value, then: (schema) => schema.min(8).test(passwordSchemaTest) }),
  password_confirmation: string()
    .label("Password Confirmation")
    .when("password", {
      is: (password) => !!password,
      then: (schema) => schema.required().oneOf([ref("password")], "${path} must match Password")
    }),
  role_id: object().when({
    is: (value) => !!value,
    then: (schema) => schema.required("Please choose role")
  })
}).required();

export const Edit = ({
  isOpen,
  onClose,
  selected: {
    isMultiOrganizations,
    userActions: { canEditUser, canUpdatePermissions },
    id
  }
}) => {
  const dispatch = useDispatch();
  const currentUserInfo = useSelector((state) => state.users.currentUserInfo);
  const {
    roles,
    permissions: allPermissions,
    locations
  } = useSelector((state) => state.users.commonSettings);

  const {
    firstName,
    lastName,
    email,
    roleId,
    phone,
    enabled2fa,
    disabled2faByEmail,
    daysInterval2fa,
    permissionIds = [],
    sendInactiveUsersEmail,
    clubreadyStoreIds = [],
    corporateReportIds = [],
    organizationInfo: { name: orgName } = {},
    user_actions: {
      canChangeUserEmail,
      canChange2FA,
      canChangeRole,
      canUpdateLocations,
      canChangeEmails,
      canUpdateCorporateReports,
      canSendInactiveUsersEmail,
      canSendLocationComparisonDailyEmail
    } = {}
  } = currentUserInfo || {};

  const selectedRole = roles.find((role) => role.id == roleId);

  const defaultValues = {
    first_name: firstName,
    last_name: lastName,
    email,
    role_id: selectedRole,
    phone_number: phone,
    enabled_2fa: enabled2fa,
    disabled_2fa_by_email: disabled2faByEmail,
    days_interval_2fa: daysInterval2fa,
    permission_ids: permissionIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    clubready_store_ids: clubreadyStoreIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    corporate_report_ids: corporateReportIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    send_inactive_users_email: sendInactiveUsersEmail,
    password: "",
    password_confirmation: ""
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, dirtyFields, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    values: defaultValues
  });

  const [activeTab, setActiveTab] = useState(0);

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  useEffect(() => {
    dispatch(fetchUserInfo(id));
  }, []);

  const { errors: serverErrors, status } = useError(() => {
    showToast("User was successfully updated");
    close();
  });

  const primaryButtonDisabled = !isDirty || status == "pending";

  const onSubmit = (data) => {
    let changedFieldKeys = Object.keys(dirtyFields);
    let formattedDataForRequest = {};

    changedFieldKeys.forEach((key) => {
      if (
        key === "clubready_store_ids" ||
        key == "permission_ids" ||
        key == "corporate_report_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: Object.keys(data[key]).map((id) => parseInt(id))
        };
      } else if (key === "role_id") {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].id
        };
      } else {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: key === "phone_number" ? data[key]?.trim() : data[key]
        };
      }
    });

    dispatch(changeUser({ data: formattedDataForRequest, id }));
  };

  // Filter out hidden permission groups (e.g., ChaseIQ Page for non-admin users)
  const filteredPermissions = filterHiddenPermissionGroups(allPermissions);
  let { permissions, daily } = rearrangeDailyEmailsPermissions(filteredPermissions);

  const activeTabClassName = "tabs__tab--active";

  const renderPermissions = () => (
    <DataPermissions
      watch={watch}
      setValue={setValue}
      allPermissions={extendPermissionByCustom(permissions, orgName)}
      dailyEmailsPermissions={daily}
      control={control}
      register={register}
      canChangeRole={canChangeRole}
      selectedRole={selectedRole}
      disableRole={isMultiOrganizations}
      canChangeEmails={canChangeEmails}
      roles={roles}
      canUpdateCorporateReports={canUpdateCorporateReports}
      canSendInactiveUsersEmail={canSendInactiveUsersEmail}
      canSendLocationComparisonDailyEmail={canSendLocationComparisonDailyEmail}
    />
  );

  const renderUserInfo = () => (
    <UserInfo
      register={register}
      control={control}
      canChange2FA={canChange2FA}
      canChangeUserEmail={canChangeUserEmail}
    />
  );

  const renderSettings = () => (
    <AccessPermissions
      control={control}
      canUpdateLocations={canUpdateLocations}
      allPermissions={{ location_permissions: locations }}
    />
  );

  const renderActiveTab = () => {
    if (activeTab === 0) return renderUserInfo();
    if (activeTab === 1) return renderPermissions();
    return renderSettings();
  };

  return (
    <>
      <MainModal
        isLoading={!currentUserInfo}
        parentSelector=".users"
        isOpen={isOpen}
        onClose={close}
        headerText={
          <>
            Edit User -{" "}
            <span className="text-bold">
              {lastName}, {firstName}
            </span>
          </>
        }
        headerCloseHandler={close}
        height="auto"
        width={900}
        primaryButtonText="Save"
        primaryBtnHandler={handleSubmit(onSubmit)}
        primaryButtonDisabled={primaryButtonDisabled}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        containerClassName="edit"
      >
        {canEditUser && canUpdatePermissions && (
          <div className="tabs">
            <div className="tabs__list">
              <button
                className={`tabs__tab ${activeTab === 0 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(0)}
              >
                User Info
              </button>
              <button
                className={`tabs__tab ${activeTab === 1 ? activeTabClassName : ""}`}
                onClick={() => setActiveTab(1)}
              >
                Data Permissions
              </button>
              {canUpdateLocations && (
                <button
                  className={`tabs__tab ${activeTab === 2 ? activeTabClassName : ""}`}
                  onClick={() => setActiveTab(2)}
                >
                  Access Permissions
                </button>
              )}
            </div>

            <div>{renderActiveTab()}</div>
          </div>
        )}
        {canEditUser && !canUpdatePermissions && renderUserInfo()}
        {canUpdatePermissions && !canEditUser && (
          <>
            {renderPermissions()}
            {renderSettings()}
          </>
        )}
        {status === "pending" && <LoadingAnimation uiLoader />}
        <ErrorList errors={{ ...errors, ...serverErrors }} />
      </MainModal>
    </>
  );
};
